/**
 * MAIN-845/849: pure helpers for the import review screen.
 *
 * Both exist because a real QA publish reported "Published, but 20 items could
 * not be applied." when in fact NOTHING was published — every accepted item
 * failed validation, the live menu was untouched and the job correctly stayed
 * in NEEDS_REVIEW. The message claimed success that never happened and gave no
 * reason, so the caterer had no way to act on it.
 */
import { getWebsiteImportProblems } from "./websiteImportProblems";

const plural = (n) => (n === 1 ? "item" : "items");

/**
 * Truthful summary of a publishMenuImportJob result. Never says "published"
 * when nothing was, and — MAIN-879 — never makes a partial success look like
 * a failure: "2 items published. 41 items could not be applied." was shown as
 * an error although two dishes had gone live.
 *
 * `remaining` is the number of imported items still not live and not rejected
 * AFTER the publish, read back from the server. Without it, the failed rows of
 * this run are what still needs attention.
 *
 * @returns {{ kind: "success"|"partial"|"none"|"already", variant, message,
 *   newlyApplied, alreadyApplied, failed, attention }}
 */
export function buildPublishResultMessage(results = [], { remaining } = {}) {
  const newlyApplied = results.filter((r) => r.status === "Applied").length;
  const alreadyApplied = results.filter((r) => r.status === "AlreadyApplied").length;
  const failed = results.filter((r) => r.status === "Failed").length;
  const attention = Number.isInteger(remaining) ? remaining : failed;
  const counts = { newlyApplied, alreadyApplied, failed, attention };
  const alreadyNote = alreadyApplied
    ? ` ${alreadyApplied} ${plural(alreadyApplied)} ${alreadyApplied === 1 ? "was" : "were"} already published.`
    : "";

  if (newlyApplied > 0 && attention === 0) {
    return {
      ...counts,
      kind: "success",
      variant: "success",
      message: `${newlyApplied} ${plural(newlyApplied)} published successfully.${alreadyNote}`,
    };
  }
  if (newlyApplied > 0) {
    return {
      ...counts,
      kind: "partial",
      variant: "warning",
      message: `${newlyApplied} ${plural(newlyApplied)} published successfully. ${attention} ${plural(attention)} still ${attention === 1 ? "needs" : "need"} attention.${alreadyNote}`,
    };
  }
  if (alreadyApplied > 0 && attention === 0 && failed === 0) {
    return {
      ...counts,
      kind: "already",
      variant: "success",
      message: "Everything in this import is already published.",
    };
  }
  return {
    ...counts,
    kind: "none",
    variant: "error",
    message: attention
      ? `No items were published. ${attention} ${plural(attention)} ${attention === 1 ? "needs" : "need"} attention.`
      : "No items were published.",
  };
}

/**
 * MAIN-879: the request itself failed (network, server error, refused lease),
 * so what happened is not known. Never claims an outcome.
 */
export function buildPublishRequestFailureMessage(error) {
  const busy = error?.graphQLErrors?.some(
    (e) => e?.extensions?.code === "MENU_IMPORT_PUBLISH_IN_PROGRESS",
  );
  return {
    variant: "error",
    message: busy
      ? "This import is already being published. Wait a moment — the list will show what went live."
      : "We couldn't confirm whether publishing finished. The list has been refreshed; check which items are live before publishing again.",
  };
}

/** Map a publish result array to { [proposalId]: errorText } for inline display. */
export function buildPublishErrorMap(results = []) {
  const map = {};
  results.forEach((r) => {
    if (r.status === "Failed" && r.proposalId) {
      map[r.proposalId] = r.error || "This item could not be applied.";
    }
  });
  return map;
}

const hasValue = (value) => value !== null && value !== undefined && value !== "";
const isSet = (value) => value !== null && value !== undefined;

// The fields a caterer can edit on a proposal — the same list as
// EDITABLE_FIELDS in api/src/modules/menuImportJob/menuImportPublish.js.
const EDITABLE_FIELDS = [
  "itemName",
  "price",
  "categoryId",
  "itemDescription",
  "dietory",
  "itemPkgType",
  "minQuantity",
];

const FIELD_LABEL = {
  itemName: "name",
  price: "price",
  categoryId: "category",
  itemDescription: "description",
  dietory: "dietary",
  itemPkgType: "serving size",
  minQuantity: "minimum quantity",
};

function sameValue(a, b) {
  // Number(null) is 0, so emptiness is settled before any numeric compare.
  if (!isSet(a) || !isSet(b)) return !isSet(a) && !isSet(b);
  if (typeof a === "number" || typeof b === "number") return Number(a) === Number(b);
  return String(a).trim() === String(b).trim();
}

/**
 * MAIN-849: the values this proposal will publish with — the caterer's edits
 * over what the AI read. Mirrors resolveEffectiveProposalFields on the server:
 * an edit that is null or missing falls back to the extracted value.
 *
 * The review card showed the extracted values only, so a caterer who set a
 * missing price to $40 still saw "Price not found" on the card.
 */
export function getEffectiveProposalFields(proposal) {
  const edited = proposal?.editedFields || {};
  const effective = {};
  EDITABLE_FIELDS.forEach((field) => {
    effective[field] = isSet(edited[field]) ? edited[field] : (proposal?.[field] ?? null);
  });
  return effective;
}

/** Fields the caterer changed from what the AI read. */
export function getEditedFieldNames(proposal) {
  const edited = proposal?.editedFields || {};
  return EDITABLE_FIELDS.filter(
    (field) => isSet(edited[field]) && !sameValue(edited[field], proposal?.[field]),
  );
}

/**
 * The category title to show. An edited category is looked up in the
 * caterer's category list; otherwise the name the AI read is kept.
 */
export function getEffectiveCategoryName(proposal, categories = []) {
  const { categoryId } = getEffectiveProposalFields(proposal);
  // MAIN-879: an item filed by synonym or inference has a real category whose
  // title differs from the document's heading ("STARTERS" -> Appetizers), so
  // the catalogue title — what it will publish under — is shown when known.
  if (categoryId) {
    const match = (categories || []).find(
      (category) => String(category._id) === String(categoryId),
    );
    if (match?.categoryTitle) return match.categoryTitle;
  }
  return proposal?.categoryName || null;
}

/**
 * MAIN-879: the category line on a card. A heading that matched no Caterplace
 * category is shown as such, so it is never mistaken for one.
 */
export function describeCardCategory(proposal, categories = []) {
  const { categoryId } = getEffectiveProposalFields(proposal);
  const name = getEffectiveCategoryName(proposal, categories);
  if (!categoryId && name) return `${name} (no matching category)`;
  return name || "Uncategorized";
}

/**
 * The Update diff as the caterer will publish it: an edited field shows the
 * caterer's value, not the AI's.
 */
export function getDisplayDiff(proposal) {
  const edited = proposal?.editedFields || {};
  return (proposal?.diff || []).map((row) =>
    isSet(edited[row.field]) && !sameValue(edited[row.field], row.newValue)
      ? { ...row, newValue: String(edited[row.field]), edited: true }
      : { ...row, edited: false },
  );
}

/**
 * Which extracted-as-missing fields still have no value once the caterer's
 * edits are applied, in the caterer's words.
 */
export function getRemainingMissingFields(proposal) {
  const effective = getEffectiveProposalFields(proposal);
  return (proposal?.missingFields || [])
    .filter((field) => !(field in effective) || !hasValue(effective[field]))
    .map((field) => FIELD_LABEL[field] || field);
}

/**
 * How a proposal's review state reads. "Edited" is its own state on the
 * server (an edit to a Pending item) and publishes only once accepted; an
 * Accepted item that is then edited stays Accepted, with its edits.
 */
export function getProposalReviewState(proposal) {
  const status = proposal?.reviewStatus || "Pending";
  const applied = Boolean(proposal?.publishedMenuId);
  const edited = status === "Edited" || getEditedFieldNames(proposal).length > 0;
  return {
    status,
    applied,
    awaitingDecision: !applied && (status === "Pending" || status === "Edited"),
    // A rejected item's edits will never be published, so they are not news.
    showEdited: edited && status !== "Rejected",
  };
}

/**
 * Review totals that add up: every proposal is counted once, as Accepted,
 * Edited (not yet accepted), Rejected or still Pending.
 */
export function getReviewCounts(proposals = []) {
  const counts = { total: proposals.length, accepted: 0, edited: 0, rejected: 0, pending: 0 };
  proposals.forEach((proposal) => {
    if (proposal.reviewStatus === "Accepted") counts.accepted += 1;
    else if (proposal.reviewStatus === "Edited") counts.edited += 1;
    else if (proposal.reviewStatus === "Rejected") counts.rejected += 1;
    else counts.pending += 1;
  });
  return counts;
}

function normalizeName(value) {
  return String(value == null ? "" : value)
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

/**
 * Why this proposal cannot go live yet, one entry per problem, in the
 * caterer's words — MAIN-879: with the field the editor should focus, and
 * whether the editor can fix it at all (an open-order hold cannot be).
 * Mirrors the server rules in api/src/modules/menu/menu-publish-state.js:
 * a published item needs a category, a price greater than 0 and a minimum
 * quantity greater than 0.
 *
 * A caterer's own edit wins over the extracted value, matching
 * resolveEffectiveProposalFields on the server. An Update against an existing
 * menu item only writes the changed fields, so the create-time requirements
 * are not applied to it. An item that is already live has nothing to fix.
 *
 * @returns {{ field: string|null, message: string, fixable: boolean }[]}
 */
export function getPublishProblems(proposal) {
  if (!proposal || proposal.publishedMenuId) return [];

  if (proposal.classification === "Blocked") {
    return [{ field: null, message: "Held by an open order", fixable: false }];
  }

  // MAIN-879 website imports add their own limits (currency, customization
  // and, for updates, a usable price and minimum).
  const websiteProblems = getWebsiteImportProblems(proposal);

  // An Update patches an item that already exists and already satisfies these.
  const isUpdate =
    proposal.classification === "Update" && proposal.matchedMenuId;
  if (isUpdate) return websiteProblems;

  const problems = [...websiteProblems];
  const edited = proposal.editedFields || {};
  const itemName = hasValue(edited.itemName)
    ? edited.itemName
    : proposal.itemName;
  const categoryId = hasValue(edited.categoryId)
    ? edited.categoryId
    : proposal.categoryId;
  const price = hasValue(edited.price) ? edited.price : proposal.price;
  const minQuantity = hasValue(edited.minQuantity)
    ? edited.minQuantity
    : proposal.minQuantity;

  if (!hasValue(categoryId)) {
    problems.push({ field: "categoryId", message: "Category is required", fixable: true });
  }
  if (!hasValue(price) || !(Number(price) > 0)) {
    problems.push({ field: "price", message: "Price is required", fixable: true });
  }
  if (
    !hasValue(minQuantity) ||
    !Number.isSafeInteger(Number(minQuantity)) ||
    Number(minQuantity) <= 0
  ) {
    problems.push({ field: "minQuantity", message: "Minimum quantity is required", fixable: true });
  }
  if (proposal.classification === "Duplicate" && proposal.matchedMenuItem) {
    const stillSameName =
      normalizeName(itemName) === normalizeName(proposal.matchedMenuItem.itemName);
    const stillSameCategory =
      hasValue(categoryId) &&
      hasValue(proposal.matchedMenuItem.categoryId) &&
      String(categoryId) === String(proposal.matchedMenuItem.categoryId);
    if (stillSameName && stillSameCategory) {
      problems.push({
        field: "itemName",
        message: "Change the name or category to keep both",
        fixable: true,
      });
    }
  }
  return problems;
}

/** The problems' messages only, as the review screen has always listed them. */
export function getPublishBlockers(proposal) {
  return getPublishProblems(proposal).map((problem) => problem.message);
}

const isLive = (p) => Boolean(p?.publishedMenuId);

/**
 * Accepted proposals that would fail publish as they stand. MAIN-879: an
 * item already published is never counted again.
 */
export function getIncompleteAccepted(proposals = []) {
  return proposals.filter(
    (p) => p.reviewStatus === "Accepted" && !isLive(p) && getPublishBlockers(p).length > 0,
  );
}

/**
 * Accepted proposals that can actually go live right now — and have not gone
 * live yet. MAIN-879: an applied proposal stays Accepted on the server, so
 * counting it here is what left the button reading "Publish 2" after one of
 * the two had been published.
 */
export function getReadyToPublish(proposals = []) {
  return proposals.filter(
    (p) => p.reviewStatus === "Accepted" && !isLive(p) && getPublishBlockers(p).length === 0,
  );
}

/**
 * MAIN-879: the one definition of every import counter, derived from the
 * persisted proposals, so the footer, the Publish button and the menu page
 * banner can never disagree.
 *
 * - total:          every proposal the import produced (never shrinks)
 * - published:      live on the menu
 * - rejected:       turned down by the caterer
 * - remaining:      neither live nor rejected — the work still left
 * - awaitingReview: remaining and not yet accepted (Pending or Edited)
 * - readyToPublish: accepted and publishable now
 * - needsFixes:     accepted but missing something it needs to go live
 * - blocked:        remaining and held by an open order
 */
export function getImportCounts(proposals = []) {
  const counts = {
    total: proposals.length,
    published: 0,
    rejected: 0,
    remaining: 0,
    awaitingReview: 0,
    readyToPublish: 0,
    needsFixes: 0,
    blocked: 0,
  };
  proposals.forEach((p) => {
    if (isLive(p)) {
      counts.published += 1;
      return;
    }
    if (p.reviewStatus === "Rejected") {
      counts.rejected += 1;
      return;
    }
    counts.remaining += 1;
    if (p.classification === "Blocked") counts.blocked += 1;
    if (p.reviewStatus === "Accepted") {
      if (getPublishBlockers(p).length) counts.needsFixes += 1;
      else counts.readyToPublish += 1;
    } else {
      counts.awaitingReview += 1;
    }
  });
  return counts;
}

/**
 * MAIN-849 (first-time import simplified): which classification chips to show.
 *
 * A caterer importing into an empty menu has nothing to reconcile — every item
 * is New — so a row reading "Possible duplicate 0 · Update to an existing item
 * 0 · Can't change yet 0" is pure noise. Only classifications that actually
 * occur are offered.
 *
 * The active filter is always kept, even at zero: rejecting the last Duplicate
 * while filtered to Duplicate would otherwise remove the chip under the
 * caterer and strand them on a filter they can no longer see or leave.
 * "All" is rendered separately and never hidden.
 */
export function getVisibleClassificationChips(
  classifications = [],
  counts = {},
  activeFilter = "All",
) {
  return classifications.filter(
    (key) => counts[key] > 0 || key === activeFilter,
  );
}
